import { useId, type ReactNode } from 'react'
import { cx } from './primitives'

/**
 * Scoped tabs, after the SLDS pattern: a bordered panel whose active tab joins the body
 * via a matching background and a top accent rule.
 *
 * Responsive behaviour — the tab strip scrolls horizontally with snap points rather than
 * wrapping or collapsing into a select. Wrapping breaks the "joined to the panel" illusion
 * the scoped pattern depends on, and a select hides the other tabs entirely, which is the
 * thing tabs exist to avoid. Labels shorten below `sm`; icons carry the meaning there.
 */

export type TabDef = {
  id: string
  label: string
  shortLabel?: string
  icon?: ReactNode
  badge?: ReactNode
  /**
   * This tab's panel. Required with `keepMounted`, which renders every panel; without it
   * the active panel comes from `children` instead.
   */
  content?: ReactNode
}

export function ScopedTabs({
  tabs, activeId, onChange, children, variant = 'card', label = 'Project sections',
  measure = false, keepMounted = false,
}: {
  tabs: TabDef[]
  activeId: string
  onChange: (id: string) => void
  /** The active panel's content. Unused — and unnecessary — with `keepMounted`. */
  children?: ReactNode
  /**
   * `card` is the standalone panel used for the main navigation. `inline` drops the
   * outer border and the panel tint for a strip that sits inside something that already
   * has chrome of its own — a drawer — where a second bordered box reads as clutter.
   * The tab behaviour, including the keyboard pattern, is identical.
   */
  variant?: 'card' | 'inline'
  /** What this set of tabs navigates, for screen readers. */
  label?: string
  /**
   * Hold the strip and the panel to a readable measure, centred. For the full-window
   * drawer, where the viewport is far wider than any line of prose should be — the rules
   * of the panel still run the whole width, so the constraint reads as a column rather
   * than as a narrow box floating in space.
   */
  measure?: boolean
  /**
   * Render every panel and hide the inactive ones, rather than mounting only the active
   * tab. Unmounting is the better default — it keeps subscriptions off screens nobody is
   * looking at — but inside a drawer it throws away whatever somebody had half-typed the
   * moment they glance at another tab. Requires each tab to carry its own `content`.
   */
  keepMounted?: boolean
}) {
  const baseId = useId()

  // Roving arrow-key navigation, per the WAI-ARIA tabs pattern.
  const onKeyDown = (event: React.KeyboardEvent) => {
    const index = tabs.findIndex((t) => t.id === activeId)
    if (index < 0) return
    const last = tabs.length - 1
    let next: number | null = null
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = last
    if (next === null) return
    event.preventDefault()
    const target = tabs[next]
    if (!target) return
    onChange(target.id)
    // `useId` produces ids containing colons, which a CSS selector cannot carry unescaped
    // — and `CSS.escape` is not present in every environment the tests run in. Looking the
    // element up by id avoids needing a selector at all.
    document.getElementById(`${baseId}-tab-${target.id}`)?.focus()
  }

  const inline = variant === 'inline'

  return (
    <div className={cx(
      inline ? 'flex min-h-0 flex-1 flex-col' : 'overflow-hidden rounded-lg border border-mc-line bg-mc-surface',
    )}>
      <div
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
        className={cx(
          'flex snap-x snap-mandatory overflow-x-auto border-b border-mc-line',
          inline ? 'shrink-0 bg-mc-surface' : 'bg-mc-surface-2',
          measure && 'mx-auto w-full max-w-5xl',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        {tabs.map((tab) => {
          const selected = tab.id === activeId
          return (
            <button
              key={tab.id}
              id={`${baseId}-tab-${tab.id}`}
              role="tab"
              type="button"
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(tab.id)}
              className={cx(
                'relative flex shrink-0 snap-start items-center gap-2 border-r border-mc-line',
                'text-sm font-medium whitespace-nowrap transition-colors',
                inline ? 'px-3 py-2.5 sm:px-4' : 'px-3 py-3 sm:px-5',
                'min-h-[44px]', // comfortable touch target on mobile
                selected
                  ? inline
                    ? 'bg-mc-surface-2 text-mc-primary'
                    : 'bg-mc-surface text-mc-primary'
                  : 'text-mc-muted hover:bg-mc-surface-2 hover:text-mc-primary',
              )}
            >
              {/* The accent rule that reads as "this tab owns the panel below". */}
              <span
                aria-hidden
                className={cx(
                  'absolute inset-x-0 top-0 h-[3px]',
                  selected ? 'bg-mc-accent' : 'bg-transparent',
                )}
              />
              {tab.icon}
              <span className="sm:hidden">{tab.shortLabel ?? tab.label}</span>
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.badge}
            </button>
          )
        })}
      </div>

      {tabs.map((tab) => {
        const selected = tab.id === activeId
        if (!selected && !keepMounted) return null
        return (
          <div
            key={tab.id}
            id={`${baseId}-panel-${tab.id}`}
            role="tabpanel"
            aria-labelledby={`${baseId}-tab-${tab.id}`}
            hidden={!selected}
            tabIndex={selected ? 0 : -1}
            className={cx(
              inline
                ? 'min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4 sm:px-5'
                : 'bg-mc-surface-2 p-3 sm:p-5',
              measure && 'mx-auto w-full max-w-5xl',
            )}
          >
            {keepMounted ? tab.content : children}
          </div>
        )
      })}
    </div>
  )
}
